import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/daemlbhtto0ceqqnh040";

const TOPICS = ["都可以", "深扒复盘", "热梗速递", "饭圈观察", "理性吃瓜"];

export default function Subscribe() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setStatus("idle");

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    // 蜜罐：空则不发送请求
    const honeypot = (formData.get("contact_alt") as string | null)?.trim() ?? "";
    const payload = new FormData(formEl);
    payload.delete("contact_alt");

    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();

      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = {};
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(meta?.message ?? parsed?.message ?? meta?.detail ?? "");

      const ok = response.ok && parsed?.code === "OK" && !/spam/i.test(serverMsg);
      if (ok) {
        setStatus("success");
        formEl.reset();
      } else {
        setFormError(serverMsg || "提交没有成功，请检查信息后稍后再试。");
        setStatus("error");
      }
    } catch {
      setFormError("网络开小差了，请稍后重试。");
      setStatus("error");
    }
  };

  return (
    <section id="joinform" className="scroll-mt-16 bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="overflow-hidden rounded-[2rem] border border-primary-100 bg-gradient-to-br from-primary-100/70 via-background-50 to-accent-100/70 px-6 py-10 md:px-12 md:py-14">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Left copy */}
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-background-50/90 px-3.5 py-1 text-xs font-semibold text-primary-700">
                <i className="ri-group-line text-sm" aria-hidden="true"></i>
                入群 / 订阅申请
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-900 md:text-4xl">
                别再一个人刷到半夜
                <br />
                现在就把组织找回来
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                留下你的称呼与联系方式，管理员会尽快核对并发出入群邀请。加入后新瓜预告、后续进度、同好交流一站式到位，追更从此有人一起。
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "申请免费，审核快速，随进随聊",
                  "进群先看公约，友好氛围有保障",
                  "潜水围观、随时冒泡都欢迎",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-foreground-700">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
                      <i className="ri-check-line text-sm" aria-hidden="true"></i>
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right form */}
            <div className="rounded-3xl border border-background-200 bg-background-50/95 p-6 shadow-none md:p-8">
              {status === "success" && (
                <div className="mb-5 flex items-start gap-3 rounded-xl border border-accent-200 bg-accent-100/70 px-4 py-3">
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-700" aria-hidden="true"></i>
                  <p className="text-sm text-accent-900">
                    申请已收到！管理员会尽快核对你的信息并发出入群邀请，记得留意通知哦。
                  </p>
                </div>
              )}
              {status === "error" && (
                <div className="mb-5 flex items-start gap-3 rounded-xl border border-primary-200 bg-primary-100/70 px-4 py-3">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-primary-700" aria-hidden="true"></i>
                  <p className="text-sm text-primary-900">{formError || "提交没有成功，请检查信息后稍后再试。"}</p>
                </div>
              )}

              <form data-readdy-form onSubmit={handleSubmit} noValidate={false} className="space-y-4">
                <div>
                  <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    required
                    placeholder="例如：山茶"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    常用邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收入群与后续通知"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    最想蹲的栏目
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    defaultValue="都可以"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    想先聊聊什么（可选）
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="可以简单说下你最近在蹲的话题，方便管理员帮你找到更合适的组织～"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  ></textarea>
                </div>
                {/* 蜜罐字段：人类无需填写 */}
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="contact_alt">若需联系请留</label>
                  <input
                    type="text"
                    id="contact_alt"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    className="w-full"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "success"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
                  提交申请，加入吃瓜群
                </button>
                <p className="text-center text-xs text-foreground-400">
                  提交即表示你了解并愿意遵守群内友好交流公约
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}